import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Users, MessageSquare, ThumbsUp, Star, Share2, BadgeCheck, Ear } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import CompanyStatCard from "@/components/share/CompanyStatCard";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { TRAITS, scoreColor, scoreLabel } from "@/lib/traits";
import SupervisorCard from "@/components/SupervisorCard";
import FavoriteButton from "@/components/FavoriteButton";

export default function CompanyProfile() {
  const { name } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    api.get(`/companies/${encodeURIComponent(name)}`)
      .then((r) => setCompany(r.data))
      .catch(() => setNotFound(true));
  }, [name]);

  if (notFound) return <div className="max-w-7xl mx-auto px-8 py-20 text-slate-400">Company not found.</div>;
  if (!company) return <div className="max-w-7xl mx-auto px-8 py-20 text-slate-400 font-mono">Loading...</div>;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
      <button onClick={() => navigate("/companies")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700 mb-6">
        <ArrowLeft size={16} /> All companies
      </button>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute inset-0 lb-hero-mesh opacity-60" />
          <div className="relative">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-xl bg-slate-900 flex items-center justify-center">
                <Building2 size={30} className="text-white" />
              </div>
              <div>
                <h1 data-testid="company-name" className="font-display text-4xl sm:text-5xl text-slate-900 leading-none flex items-center gap-3" style={{ fontWeight: 900 }}>
                  {company.name}
                  <FavoriteButton kind="company" refId={company.name} />
                  {company.claimed && <span data-testid="company-claimed-badge" className="text-xs font-mono uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 flex items-center gap-1"><BadgeCheck size={13} /> Verified employer</span>}
                </h1>
                <div className="flex flex-wrap gap-2 mt-3">
                  {company.departments.map((d) => (
                    <Badge key={d} variant="secondary" className="bg-slate-100 text-slate-600">{d}</Badge>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-8 mt-6">
              <div className="flex items-center gap-2 text-slate-600"><Users size={18} className="text-emerald-600" /> <b className="font-display text-xl" style={{ fontWeight: 700 }}>{company.supervisor_count}</b> leaders</div>
              <div className="flex items-center gap-2 text-slate-600"><MessageSquare size={18} className="text-emerald-600" /> <b className="font-display text-xl" style={{ fontWeight: 700 }}>{company.review_count}</b> reviews</div>
              <div className="flex items-center gap-2 text-slate-600"><ThumbsUp size={18} className="text-emerald-600" /> <b className="font-display text-xl" style={{ fontWeight: 700 }}>{company.recommend_rate}%</b> recommend</div>
              <div className="flex items-center gap-2 text-slate-600" data-testid="company-speakup"><Ear size={18} className="text-emerald-600" /> <b className="font-display text-xl" style={{ fontWeight: 700 }}>{company.speak_up_score ? company.speak_up_score.toFixed(1) : "—"}</b> speak-up</div>
            </div>
            <div className="flex flex-wrap gap-2 mt-5">
              {company.review_count > 0 && (
                <Button data-testid="company-share-card-btn" onClick={() => setShareOpen(true)} variant="outline" className="rounded-full border-slate-300 gap-1.5"><Share2 size={16} /> Share card</Button>
              )}
              {!company.claimed && (
                <Button data-testid="company-claim-cta" onClick={() => navigate("/pricing")} variant="outline" className="rounded-full border-slate-300 gap-1.5"><BadgeCheck size={16} /> Claim this company</Button>
              )}
            </div>
          </div>
        </div>

        <div className="bg-slate-900 rounded-2xl p-7 text-white flex flex-col justify-center lb-grain">
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-400 mb-2">Org-wide leadership score</p>
          <div className="flex items-end gap-2">
            <span data-testid="company-org-score" className="font-display text-7xl leading-none" style={{ fontWeight: 900 }}>
              {company.org_score > 0 ? company.org_score.toFixed(1) : "—"}
            </span>
            <span className="text-slate-400 mb-2 text-xl">/ 5</span>
          </div>
          <div className="flex items-center gap-1 mt-3">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} size={18} fill={n <= Math.round(company.org_score) ? "#F59E0B" : "transparent"} color={n <= Math.round(company.org_score) ? "#F59E0B" : "#475569"} />
            ))}
          </div>
          <p className="mt-3 text-emerald-300 font-semibold">{scoreLabel(company.org_score)}</p>
        </div>
      </div>

      {company.review_count > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-6">
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-4">Trait averages across the company</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {TRAITS.map((t) => {
              const sc = company.traits?.[t.key] || 0;
              return (
                <div key={t.key} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 text-sm text-slate-600 mb-1"><t.icon size={14} className="text-emerald-600" /> {t.short}</div>
                  <div className="font-display text-2xl" style={{ fontWeight: 700, color: scoreColor(sc) }}>{sc.toFixed(1)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <h2 className="font-display text-3xl text-slate-900 mt-10 mb-5" style={{ fontWeight: 800 }}>Supervisors at {company.name}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {company.supervisors.map((s) => <SupervisorCard key={s.id} sup={s} />)}
      </div>
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="company-share-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl" style={{ fontWeight: 800 }}>Share {company.name}</DialogTitle>
            <DialogDescription>Speak-Up score, org leadership score and trajectory — live aggregate data, no individuals.</DialogDescription>
          </DialogHeader>
          <CompanyStatCard company={company} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
